import type { AppRouter } from '@/trpc/routers/_app';
import { isTransportError } from '@/utils/user-error-message';
import { TRPCClientError } from '@trpc/client';

/** Server answers that may succeed on a later attempt, so a queued item waits for them. */
const RETRYABLE_CODES: ReadonlySet<string> = new Set([
  'UNAUTHORIZED',
  'INTERNAL_SERVER_ERROR',
  'TIMEOUT',
  'TOO_MANY_REQUESTS',
]);

/**
 * Whether a queued send should stay queued after this failure. Anything else is permanent:
 * retrying the same input cannot change the answer.
 */
export const isRetryableSendError = (error: unknown): boolean => {
  if (isTransportError(error)) return true;
  if (!(error instanceof TRPCClientError)) return false;
  const code = (error as TRPCClientError<AppRouter>).data?.code;
  return code !== undefined && RETRYABLE_CODES.has(code);
};

/** Whether the server turned the call away because the user sent too much too fast. */
export const isRateLimitError = (error: unknown): error is TRPCClientError<AppRouter> =>
  error instanceof TRPCClientError &&
  (error as TRPCClientError<AppRouter>).data?.code === 'TOO_MANY_REQUESTS';
